import React, { CSSProperties, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ============================================================
   Shared UI kit for the redesigned Jubail Report dashboard.
   Every piece reads its colors from the CSS custom properties
   defined in src/styles/designSystem.css (scoped under .jr-app),
   so it automatically follows light/dark mode with no props.
   ============================================================ */

export const T = {
    bg: 'var(--jr-bg)', surface: 'var(--jr-surface)', surface2: 'var(--jr-surface-2)', surface3: 'var(--jr-surface-3)',
    border: 'var(--jr-border)', borderStrong: 'var(--jr-border-strong)',
    text: 'var(--jr-text)', text2: 'var(--jr-text-2)', text3: 'var(--jr-text-3)', textInv: 'var(--jr-text-inv)',
    accent: 'var(--jr-accent)', accentHover: 'var(--jr-accent-hover)', accentFg: 'var(--jr-accent-fg)',
    accentSoft: 'var(--jr-accent-soft)', accentSoftBorder: 'var(--jr-accent-soft-border)',
    success: 'var(--jr-success)', successBg: 'var(--jr-success-bg)', successBorder: 'var(--jr-success-border)',
    warning: 'var(--jr-warning)', warningBg: 'var(--jr-warning-bg)', warningBorder: 'var(--jr-warning-border)',
    danger: 'var(--jr-danger)', dangerBg: 'var(--jr-danger-bg)', dangerBorder: 'var(--jr-danger-border)',
    info: 'var(--jr-info)', infoBg: 'var(--jr-info-bg)', infoBorder: 'var(--jr-info-border)',
    violet: 'var(--jr-violet)', violetBg: 'var(--jr-violet-bg)', violetBorder: 'var(--jr-violet-border)',
    chart: ['var(--jr-chart-1)', 'var(--jr-chart-2)', 'var(--jr-chart-3)', 'var(--jr-chart-4)', 'var(--jr-chart-5)', 'var(--jr-chart-6)'],
    fontSans: 'var(--jr-font-sans)', fontMono: 'var(--jr-font-mono)',
    radius: 'var(--jr-radius)', radiusSm: 'var(--jr-radius-sm)', radiusLg: 'var(--jr-radius-lg)',
    shadowSm: 'var(--jr-shadow-sm)', shadowMd: 'var(--jr-shadow-md)', shadowLg: 'var(--jr-shadow-lg)',
};

// ✅ ملاحظة TS: T مفيهاش index signature (كل مفتاح معروف بالاسم)، فأي حتة
// في الملف ده كانت بتعمل T[tone] بـ tone ديناميكي (string عادي) محتاجة cast
// صريح لـ Record<string, string> — بدون أي تغيير في القيم الفعلية الراجعة.
const TT = T as unknown as Record<string, string>;

// status -> semantic color key, so callers can pass a raw status string
export function statusTone(status?: string | null): string {
    const s = (status || '').toLowerCase();
    if (['completed', 'done', 'approved', 'paid', 'delivered', 'active', 'present', 'on track'].includes(s)) return 'success';
    if (['in progress', 'pending', 'awaiting', 'submitted', 'in transit', 'at risk'].includes(s)) return 'warning';
    if (['overdue', 'rejected', 'delayed', 'cancelled', 'critical', 'absent'].includes(s)) return 'danger';
    return 'info';
}

interface EyebrowProps {
    children: ReactNode;
    action?: ReactNode;
    onAction?: () => void;
}

export function Eyebrow({ children, action = undefined, onAction = undefined }: EyebrowProps) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span className="jr-eyebrow">{children}</span>
            {action && (
                <span onClick={onAction} style={{ fontSize: 11, fontWeight: 700, color: T.text2, cursor: 'pointer' }}>{action}</span>
            )}
        </div>
    );
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

export function Card({ children, style = undefined, className = '', ...rest }: CardProps) {
    return <div className={`jr-card ${className}`} style={{ padding: 18, ...style }} {...rest}>{children}</div>;
}

interface PillProps {
    children: ReactNode;
    tone?: string;
    icon?: ReactNode;
    style?: CSSProperties;
}

export function Pill({ children, tone = 'neutral', icon = undefined, style = undefined }: PillProps) {
    return (
        <span className={`jr-pill jr-pill-${tone}`} style={style}>
            {icon}{children}
        </span>
    );
}

interface DotProps {
    tone?: string;
    style?: CSSProperties;
}

export function Dot({ tone = 'neutral', style = undefined }: DotProps) {
    const color = tone === 'neutral' ? T.text3 : TT[tone];
    return <span className="jr-dot" style={{ background: color, ...style }} />;
}

interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    variant?: 'default' | 'primary' | 'ghost';
    size?: 'md' | 'sm';
}

export function Btn({ children, variant = 'default', size = 'md', style = undefined, ...rest }: BtnProps) {
    const cls = ['jr-btn', variant === 'primary' ? 'jr-btn-primary' : variant === 'ghost' ? 'jr-btn-ghost' : '', size === 'sm' ? 'jr-btn-sm' : ''].filter(Boolean).join(' ');
    return <button className={cls} style={style} {...rest}>{children}</button>;
}

interface ProgBarProps {
    value: number;
    tone?: string;
    height?: number;
    style?: CSSProperties;
}

export function ProgBar({ value, tone = 'accent', height = 5, style = undefined }: ProgBarProps) {
    const color = tone === 'accent' ? T.accent : TT[tone];
    return (
        <div className="jr-prog" style={{ height, ...style }}>
            <div className="jr-prog-fill" style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} />
        </div>
    );
}

interface StatTileProps {
    label: ReactNode;
    value: ReactNode;
    unit?: ReactNode;
    sub?: ReactNode;
    tone?: string;
    accentBar?: boolean;
}

export function StatTile({ label, value, unit = undefined, sub = undefined, tone = 'accent', accentBar = true }: StatTileProps) {
    const color = tone === 'accent' ? T.text : TT[tone];
    return (
        <div style={{ padding: '14px 16px', borderRight: `1px solid ${T.border}`, position: 'relative', background: T.surface, minWidth: 0 }}>
            {accentBar && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: tone === 'accent' ? T.accent : TT[tone] }} />}
            <div className="jr-eyebrow" style={{ marginBottom: 6 }}>{label}</div>
            <div className="jr-mono" style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1, color, marginBottom: 4 }}>
                {value}{unit && <span style={{ fontSize: 11, color: T.text3, marginLeft: 3, fontWeight: 500 }}>{unit}</span>}
            </div>
            {sub && <div style={{ fontSize: 11, color: T.text3, fontWeight: 500 }}>{sub}</div>}
        </div>
    );
}

interface MiniStatProps {
    value: ReactNode;
    label: ReactNode;
    tone?: string;
}

export function MiniStat({ value, label, tone = 'accent' }: MiniStatProps) {
    const color = tone === 'accent' ? T.text : TT[tone];
    return (
        <div style={{ background: T.surface2, borderRadius: T.radiusSm, padding: '10px 12px', border: `1px solid ${T.border}` }}>
            <div className="jr-mono" style={{ fontSize: 18, fontWeight: 700, lineHeight: 1, color, marginBottom: 3 }}>{value}</div>
            <div className="jr-eyebrow">{label}</div>
        </div>
    );
}

interface EmptyStateProps {
    icon?: ReactNode;
    title?: ReactNode;
    sub?: ReactNode;
}

export function EmptyState({ icon = undefined, title = 'No data', sub = undefined }: EmptyStateProps) {
    return (
        <div style={{ padding: '28px 16px', textAlign: 'center', color: T.text3 }}>
            {icon && <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.6 }}>{icon}</div>}
            <div style={{ fontSize: 12, fontWeight: 600 }}>{title}</div>
            {sub && <div style={{ fontSize: 11, marginTop: 4 }}>{sub}</div>}
        </div>
    );
}

interface AvatarProps {
    name?: string | null;
    src?: string | null;
    size?: number;
    color?: string;
}

export function Avatar({ name, src = undefined, size = 28, color = undefined }: AvatarProps) {
    const bg = color || T.chart[Math.abs((name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % T.chart.length];
    return (
        <div title={name ?? undefined} style={{
            width: size, height: size, borderRadius: size * 0.28, background: bg, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: size * 0.36, fontWeight: 700, color: '#fff', overflow: 'hidden', flexShrink: 0,
        }}>
            {src ? <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (name || '?').charAt(0).toUpperCase()}
        </div>
    );
}

// Tooltip style object for recharts <Tooltip contentStyle={chartTooltipStyle}/>
export const chartTooltipStyle = {
    background: 'var(--jr-surface)',
    border: '1px solid var(--jr-border)',
    borderRadius: 8,
    fontSize: 11,
    fontFamily: 'var(--jr-font-sans)',
    color: 'var(--jr-text)',
    boxShadow: 'var(--jr-shadow-md)',
};

export const chartAxisStyle = { fill: 'var(--jr-text-3)', fontSize: 10, fontWeight: 500 };
export const chartGridStroke = 'var(--jr-border)';

/* ============================================================
   Modal / ConfirmDialog / Toast — added for the ITSM rebuild so
   every page can drop window.alert()/window.confirm() in favor
   of something that actually matches the design system. These
   are generic (not ITSM-specific) so any page in the app can use
   them; they don't need a provider mounted at the root — Toast
   is a small self-contained pub/sub since ITSM/client pages are
   independent top-level routes, not children of App.jsx.
   ============================================================ */

interface ModalProps {
    open: boolean;
    onClose?: () => void;
    title?: ReactNode;
    children: ReactNode;
    footer?: ReactNode;
    width?: number;
}

export function Modal({ open, onClose, title, children, footer, width = 480 }: ModalProps) {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={onClose}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 8 }}
                        onClick={e => e.stopPropagation()}
                        className="jr-card"
                        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflowY: 'auto', padding: 0, background: T.surface }}>
                        {title && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
                                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{title}</div>
                                <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text3, fontSize: 18, lineHeight: 1, padding: 4 }}>✕</button>
                            </div>
                        )}
                        <div style={{ padding: 20 }}>{children}</div>
                        {footer && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: `1px solid ${T.border}` }}>{footer}</div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

interface ConfirmDialogProps {
    open: boolean;
    onClose?: () => void;
    onConfirm?: () => void;
    title?: ReactNode;
    message?: ReactNode;
    confirmLabel?: ReactNode;
    cancelLabel?: ReactNode;
    danger?: boolean;
}

export function ConfirmDialog({ open, onClose, onConfirm, title = 'Are you sure?', message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false }: ConfirmDialogProps) {
    return (
        <Modal open={open} onClose={onClose} title={title} width={380}
            footer={
                <>
                    <Btn variant="ghost" onClick={onClose}>{cancelLabel}</Btn>
                    <Btn variant="primary" style={danger ? { background: T.danger, borderColor: T.danger } : undefined}
                        onClick={() => { onConfirm?.(); onClose?.(); }}>{confirmLabel}</Btn>
                </>
            }>
            <div style={{ fontSize: 13, color: T.text2, lineHeight: 1.6 }}>{message}</div>
        </Modal>
    );
}

interface ToastItem {
    id: number;
    message: ReactNode;
    type: string;
}

type ToastListener = (toast: ToastItem) => void;

let _toastListeners: ToastListener[] = [];
let _toastIdCounter = 0;

// showToast('Saved', 'success') — call from anywhere; mount <ToastHost/>
// once per page (typically at the root of each page component) to render.
export function showToast(message: ReactNode, type: string = 'info') {
    const id = ++_toastIdCounter;
    const toast: ToastItem = { id, message, type };
    _toastListeners.forEach(fn => fn(toast));
    return id;
}

const TOAST_TONE: Record<string, string> = { success: 'success', error: 'danger', warning: 'warning', info: 'info' };

export function ToastHost() {
    const [toasts, setToasts] = React.useState<ToastItem[]>([]);

    React.useEffect(() => {
        const handler: ToastListener = (toast) => {
            setToasts(prev => [...prev, toast]);
            setTimeout(() => setToasts(prev => prev.filter(t => t.id !== toast.id)), 3500);
        };
        _toastListeners.push(handler);
        return () => { _toastListeners = _toastListeners.filter(l => l !== handler); };
    }, []);

    return (
        <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 2000, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
            <AnimatePresence>
                {toasts.map(t => {
                    const tone = TOAST_TONE[t.type] || 'info';
                    return (
                        <motion.div key={t.id}
                            initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                            style={{
                                pointerEvents: 'auto', background: T.surface, border: `1px solid ${TT[tone + 'Border']}`,
                                color: T.text, borderRadius: T.radius, padding: '10px 14px', fontSize: 13, fontWeight: 600,
                                boxShadow: T.shadowLg, minWidth: 220, maxWidth: 360, display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                            <Dot tone={tone} />
                            {t.message}
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}
